import {useEffect, useState} from 'react';
import {doc, getDoc, onSnapshot, runTransaction} from 'firebase/firestore';
import {auth, db} from '../../Security/firebase-config';
import {canEditCompositionPresets, presetMapKey, updatePresetMap, readPresetMap} from './compositionPresets';
const blank = {presets:[], mainId:'', secondaryId:'', revision:''};
export default function useCompositionPresets(teamId, mapName, userUid) {
  const uid = userUid || auth?.currentUser?.uid;
  const identity = JSON.stringify([teamId, mapName, uid]);
  const [state, setState] = useState({identity, loading:true, data:blank, members:[], team:{}, error:''});
  useEffect(() => {
    let alive = true, rosterVersion = 0;
    const loaded = new Set();
    const update = changes => {if (alive) setState(previous => ({...previous, ...changes}));};
    const done = part => {loaded.add(part); if (loaded.size === 2) update({loading:false});};
    setState({identity, loading:true, data:blank, members:[], team:{}, error:''});
    if (!teamId || !mapName || !uid) {update({loading:false}); return undefined;}
    const fail = failure => update({data:blank,members:[],team:{},loading:false, error:failure.code === 'permission-denied' ? 'Saved compositions are unavailable. Check team access and database rules.' : failure.message});
    const stops = [
      onSnapshot(doc(db,'teams',teamId), snap => {
        const team = snap.data() || {}, version = ++rosterVersion;
        update({team});
        Promise.all((team.members || []).map(async member => {
          const id = typeof member === 'string' ? member : member.uid;
          if (!id) return null;
          const profile = await getDoc(doc(db,'uids',id)).catch(() => null);
          return {uid:id, username:profile?.data()?.username || team.memberUsernames?.[id] || member.username || id, role:team.teamRoles?.[id] || 'Spectator'};
        })).then(members => {if (alive && version === rosterVersion) {update({members:members.filter(Boolean)}); done('team');}}).catch(fail);
      }, fail),
      onSnapshot(doc(db,'teams',teamId,'compositionPresets',presetMapKey(mapName)), snap => {try {update({data:readPresetMap(snap.data())}); done('map');} catch(failure) {fail(failure);}}, fail),
    ];
    return () => {alive=false; stops.forEach(stop => stop());};
  }, [identity, teamId, mapName, uid]);
  const current = state.identity === identity ? state : {loading:true, data:blank, members:[], team:{}, error:''};
  const canEdit = !current.error && !current.loading && canEditCompositionPresets(current.team,uid);
  const save = async (preset, designation, remove=false, expectedRevision=current.data.revision) => {
    const ref = doc(db,'teams',teamId,'compositionPresets',presetMapKey(mapName));
    await runTransaction(db, async transaction => {
      const teamSnap = await transaction.get(doc(db,'teams',teamId));
      const existing = await transaction.get(ref), data = readPresetMap(existing.data());
      const team = teamSnap.data() || {}, ids = (team.members || []).map(member => typeof member === 'string' ? member : member.uid);
      if (!canEditCompositionPresets(team,uid)) throw new Error('You no longer have permission to edit saved compositions.');
      if (data.revision !== expectedRevision) throw new Error('Another member updated these compositions. Review the latest version and try again.');
      const members = current.members.filter(member => ids.includes(member.uid));
      const next = remove ? {...data, presets:data.presets.filter(item => item.id !== preset.id), mainId:data.mainId === preset.id ? '' : data.mainId, secondaryId:data.secondaryId === preset.id ? '' : data.secondaryId} : updatePresetMap(data,preset,designation,members);
      transaction.set(ref,{mapName, presetCount:next.presets.length, content:JSON.stringify({presets:next.presets,mainId:next.mainId,secondaryId:next.secondaryId}), revision:crypto.randomUUID(), updatedByUid:uid, updatedAt:new Date().toISOString()});
    });
  };
  return {...current, canEdit, save};
}
